import imageCompression from 'browser-image-compression';

interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  maxIteration?: number;
  initialQuality?: number;
  fileType?: string;
}

/**
 * Compress an image file if it exceeds the size threshold
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns The compressed file (or original if under threshold)
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const {
    maxSizeMB = 0.2, // 200KB default threshold
    maxWidthOrHeight = 1920, // Suitable for property images
    useWebWorker = true,
    maxIteration = 10,
    initialQuality = 0.8,
    fileType = file.type
  } = options;

  // If file is already under the threshold, return as-is
  const fileSizeMB = file.size / 1024 / 1024;
  if (fileSizeMB <= maxSizeMB) {
    console.log(`Image ${file.name} is already under ${maxSizeMB}MB (${fileSizeMB.toFixed(2)}MB), skipping compression`);
    return file;
  }

  console.log(`Compressing image ${file.name} from ${fileSizeMB.toFixed(2)}MB to under ${maxSizeMB}MB`);

  try {
    const compressedFile = await imageCompression(file, {
      maxSizeMB,
      maxWidthOrHeight,
      useWebWorker,
      maxIteration,
      initialQuality,
      fileType,
      // Preserve metadata for property images
      preserveExif: true,
      // Progress callback
      onProgress: (progress) => {
        console.log(`Compression progress: ${Math.round(progress)}%`);
      }
    });

    const compressedSizeMB = compressedFile.size / 1024 / 1024;
    const compressionRatio = ((fileSizeMB - compressedSizeMB) / fileSizeMB * 100).toFixed(1);
    
    console.log(`Successfully compressed ${file.name}:`);
    console.log(`  Original: ${fileSizeMB.toFixed(2)}MB`);
    console.log(`  Compressed: ${compressedSizeMB.toFixed(2)}MB`);
    console.log(`  Reduction: ${compressionRatio}%`);

    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    // Return original file if compression fails
    return file;
  }
}

/**
 * Check if a file type supports compression
 */
export function isCompressibleImageType(mimeType: string): boolean {
  const compressibleTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'image/bmp'
  ];
  return compressibleTypes.includes(mimeType.toLowerCase());
}

/**
 * Get recommended compression settings based on image purpose
 */
export function getCompressionSettings(purpose: string): CompressionOptions {
  switch (purpose) {
    case 'background':
    case 'hero':
      return {
        maxSizeMB: 0.5, // 500KB for hero images
        maxWidthOrHeight: 1920,
        initialQuality: 0.85
      };
    
    case 'gallery':
      return {
        maxSizeMB: 0.3, // 300KB for gallery images
        maxWidthOrHeight: 1200,
        initialQuality: 0.8
      };
    
    case 'thumbnail':
      return {
        maxSizeMB: 0.1, // 100KB for thumbnails
        maxWidthOrHeight: 600,
        initialQuality: 0.7
      };
    
    default:
      return {
        maxSizeMB: 0.2, // 200KB default
        maxWidthOrHeight: 1920,
        initialQuality: 0.8
      };
  }
} 